import React, { useEffect, useState, useMemo } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import useSubmitGuard from "../lib/useSubmitGuard";
import PageHeader from "../components/PageHeader";
import { usePager } from "../components/Pagination";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Plus, Printer, AlertTriangle, Trash2, RotateCcw, Wrench, Ban, ScanLine, X } from "lucide-react";
import { Dialog, Input } from "./Suppliers";
import { fmtIST } from "../lib/time";

const CONDITIONS = [
  ["good", "Good", "#2E7D32"],
  ["under_maintenance", "Under Maintenance", "#E65100"],
  ["damaged", "Damaged", "#C62828"],
];

const EMPTY = () => ({
  asset_number: "",
  material_id: "",
  description: "",
  location: "",
  condition: "good",
  condition_change_reason: "",
  calibration_required: false,
  calibration_due_date: "",
});

export default function AssetRegister() {
  const { busy: submitBusy, guard: submitGuard, reset: resetGuard } = useSubmitGuard();
  const { user } = useAuth();
  const isPHorSA = !!user?.role?.is_super_admin || user?.role?.name === "Plant Head/Admin";
  const isSA = !!user?.role?.is_super_admin;
  const [rows, setRows] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY());
  const [available, setAvailable] = useState(null);   // duplicate check
  const [scrap, setScrap] = useState(null);            // {asset, reason}
  const [cycle, setCycle] = useState(null);
  const [scanMode, setScanMode] = useState(false);
  const [scanInput, setScanInput] = useState("");
  const [scanFeedback, setScanFeedback] = useState(null);
  const [printReq, setPrintReq] = useState(null);

  const load = async () => {
    const [r, m, c] = await Promise.all([
      api.get("/assets", { params: { include_scrapped: false } }),
      api.get("/materials"),
      api.get("/assets/reconcile/current").catch(() => ({ data: { cycle: null } })),
    ]);
    setRows(r.data || []);
    setMaterials(m.data || []);
    setCycle(c.data);
  };
  useEffect(() => { load(); }, []);

  // Real-time duplicate check on the asset number input (500ms debounce)
  useEffect(() => {
    if (!open) return;
    const n = (form.asset_number || "").toUpperCase();
    if (!n) { setAvailable(null); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/assets/check-number?number=${encodeURIComponent(n)}`);
        setAvailable(r.data);
      } catch { setAvailable(null); }
    }, 500);
    return () => clearTimeout(t);
  }, [form.asset_number, open]);

  const save = async () => {
    if (!(form.asset_number || "").trim() || !form.location.trim()) {
      toast.error("Asset number and Location are required."); return;
    }
    if (available && available.available === false) {
      toast.error(available.reason || "Asset number not available."); return;
    }
    if (["damaged", "under_maintenance"].includes(form.condition) && !form.condition_change_reason.trim()) {
      toast.error("Reason mandatory for Damaged / Under Maintenance."); return;
    }
    try {
      await submitGuard(async (idemKey) => {
        await api.post("/assets", { ...form, asset_number: form.asset_number.toUpperCase().trim() }, { headers: { "Idempotency-Key": idemKey } });
      });
      toast.success("Asset registered");
      setOpen(false); setForm(EMPTY()); resetGuard(); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const requestScrap = async () => {
    if (!(scrap?.reason || "").trim()) { toast.error("Reason required (min 5 chars)"); return; }
    try {
      await api.post(`/assets/${scrap.asset.id}/scrap-request`, { reason: scrap.reason });
      toast.success("Scrap request sent to Super Admin");
      setScrap(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const decideScrap = async (aid, action, note) => {
    try {
      await api.post(`/assets/${aid}/scrap-${action}`, { note: note || "" });
      toast.success(`Scrap ${action}d`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const doScan = async (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const code = (scanInput || "").split("|")[0].trim();   // QR is `asset|mat|name|loc`
    if (!code) return;
    try {
      const r = await api.post("/assets/reconcile/scan", { asset_number: code });
      setScanFeedback({ ok: true, msg: `Verified ${r.data.asset_number}` });
      setScanInput("");
      load();
    } catch (err) {
      setScanFeedback({ ok: false, msg: formatApiErrorDetail(err.response?.data?.detail) || "Invalid asset" });
    }
    setTimeout(() => setScanFeedback(null), 2500);
  };

  const startCycle = async () => {
    try { await api.post("/assets/reconcile/start"); toast.success("Reconciliation cycle started"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const closeCycle = async () => {
    if (!window.confirm("Close current reconciliation cycle? Unscanned active assets will be archived as Missing.")) return;
    try { await api.post("/assets/reconcile/close"); toast.success("Cycle closed"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const extendCycle = async () => {
    try { await api.post("/assets/reconcile/extend", { days: 2 }); toast.success("Extended by 2 days"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const requestReprint = async () => {
    if (!(printReq?.reason || "").trim()) { toast.error("Reason required"); return; }
    try {
      await api.post(`/assets/${printReq.asset.id}/print-request`, { reason: printReq.reason });
      toast.success("Reprint request sent to Plant Head / Admin");
      setPrintReq(null);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  // Wave-J pagination
  const { visibleRows: pagedAssets, controls: assetControls } = usePager(rows, 10);

  return (
    <div data-testid="asset-register-page">
      <PageHeader title="Asset Register" subtitle="Free-text asset numbers · Condition-tracked · TSPL2 stickers · Quarterly reconciliation"
        helpText="Asset numbers are uppercase alphanumeric+hyphens, 3-30 chars, never reused. First sticker print is free for Store; reprints need PH approval. Scrap goes through SA."
        right={<button onClick={() => setOpen(true)} className="bg-[#1565C0] hover:bg-[#0D2B5E] text-white text-sm px-4 py-2 rounded-sm flex items-center gap-2 uppercase tracking-wider font-semibold" data-testid="new-asset-btn"><Plus size={14}/> New Asset</button>}/>
      <div className="p-8 space-y-6">
        {/* Reconciliation banner */}
        <section className="bg-white border border-[#EEEEEE] rounded-sm">
          <div className="px-5 py-3 flex items-center justify-between bg-[#F5F5F5] border-b border-[#EEEEEE]">
            <div className="flex items-center gap-2">
              <ScanLine size={14}/>
              <div className="text-[10px] tracking-[0.2em] font-bold text-[#0D2B5E] uppercase">Reconciliation</div>
              {cycle?.cycle && <span className="text-[10px] uppercase tracking-widest text-[#2E7D32] font-bold">CYCLE OPEN · {cycle.cycle.number}</span>}
            </div>
            <div className="flex gap-2">
              {!cycle?.cycle && isPHorSA && (
                <button onClick={startCycle} data-testid="start-cycle-btn" className="bg-[#2E7D32] text-white text-[10px] tracking-widest uppercase font-bold px-3 py-1 rounded-sm">Start Cycle</button>
              )}
              {cycle?.cycle && <button onClick={() => setScanMode(!scanMode)} data-testid="toggle-scan-mode" className="bg-[#1565C0] text-white text-[10px] tracking-widest uppercase font-bold px-3 py-1 rounded-sm">{scanMode ? "Hide Scanner" : "Open Scanner"}</button>}
              {cycle?.cycle && isSA && cycle.past_grace && <button onClick={extendCycle} data-testid="extend-cycle-btn" className="bg-[#E65100] text-white text-[10px] tracking-widest uppercase font-bold px-3 py-1 rounded-sm">Extend 2d</button>}
              {cycle?.cycle && isSA && <button onClick={closeCycle} data-testid="close-cycle-btn" className="bg-[#C62828] text-white text-[10px] tracking-widest uppercase font-bold px-3 py-1 rounded-sm">Close Cycle</button>}
            </div>
          </div>
          {cycle?.cycle && (
            <div className="px-5 py-3 grid grid-cols-4 gap-4 text-sm">
              <KV k="Total Assets" v={cycle.total}/>
              <KV k="Scanned" v={`${cycle.scanned_count}/${cycle.total}`} color="#2E7D32"/>
              <KV k="Missing" v={cycle.missing_count} color={cycle.missing_count ? "#C62828" : "#2E7D32"}/>
              <KV k="Grace ends" v={fmtIST(cycle.cycle.grace_ends_at)}/>
              {scanMode && (
                <div className="col-span-4 bg-[#0D2B5E] text-white p-4 rounded-sm" data-testid="asset-scan-zone">
                  <div className="text-[10px] tracking-widest uppercase text-white/60 mb-2">Scan asset QR or type asset#:</div>
                  <input autoFocus value={scanInput} onChange={(e) => setScanInput(e.target.value)} onKeyDown={doScan} data-testid="asset-scan-input"
                    placeholder="ASSET-001 or scan QR…"
                    className="w-full text-2xl font-mono text-white bg-transparent border-b-2 border-white/30 focus:border-[#2E7D32] outline-none py-2 tracking-widest"/>
                  {scanFeedback && (
                    <div className={`mt-2 text-sm font-bold ${scanFeedback.ok ? "text-[#90EE90]" : "text-[#FFB74D]"}`} data-testid="asset-scan-feedback">{scanFeedback.msg}</div>
                  )}
                </div>
              )}
            </div>
          )}
        </section>

        {/* Asset list */}
        <section className="bg-white border border-[#EEEEEE] rounded-sm">
          <div className="px-5 py-3 border-b border-[#EEEEEE] flex items-center justify-between bg-[#F5F5F5]">
            <div className="text-[10px] tracking-[0.2em] font-bold text-[#0D2B5E] uppercase">Registered Assets ({rows.length})</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F5F5F5] text-[10px] uppercase tracking-widest text-[#0D2B5E]/60">
                <tr>
                  <th className="text-left px-4 py-2">Asset #</th>
                  <th className="text-left px-4 py-2">Material</th>
                  <th className="text-left px-4 py-2">Location</th>
                  <th className="text-left px-4 py-2">Condition</th>
                  <th className="text-left px-4 py-2">Calibration</th>
                  <th className="text-left px-4 py-2">Registered</th>
                  <th className="text-left px-4 py-2">Last Scan</th>
                  <th className="text-right px-4 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagedAssets.map((a) => {
                  const cond = CONDITIONS.find(([v]) => v === a.condition) || ["good", "Good", "#2E7D32"];
                  return (
                    <tr key={a.id} className="border-t border-[#EEEEEE]" data-testid={`asset-row-${a.asset_number}`}>
                      <td className="px-4 py-2 font-mono font-bold text-[#0D2B5E]">{a.asset_number}</td>
                      <td className="px-4 py-2 text-xs">{a.material_code ? `${a.material_code} — ${a.material_name}` : (a.description || "—")}</td>
                      <td className="px-4 py-2 text-xs">{a.location}</td>
                      <td className="px-4 py-2">
                        <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-sm text-white" style={{ backgroundColor: cond[2] }}>{cond[1]}</span>
                        {a.scrap_request_status === "pending" && <div className="text-[10px] text-[#E65100] font-bold mt-1">SCRAP PENDING</div>}
                      </td>
                      <td className="px-4 py-2 text-xs">
                        {a.calibration_required ? (
                          <span className={`font-mono ${a.calibration_flag === "overdue" ? "text-[#C62828] font-bold" : a.calibration_flag === "due_soon" ? "text-[#E65100] font-bold" : "text-[#0D2B5E]/70"}`}>
                            {a.calibration_due_date || "—"}{a.calibration_flag === "overdue" && " · OVERDUE"}{a.calibration_flag === "due_soon" && " · <30d"}
                          </span>
                        ) : "—"}
                      </td>
                      <td className="px-4 py-2 text-[10px] font-mono text-[#0D2B5E]/60">{fmtIST(a.registered_at)?.slice(0, 10)}</td>
                      <td className="px-4 py-2 text-[10px] font-mono text-[#0D2B5E]/60">{a.last_scanned_at ? fmtIST(a.last_scanned_at) : "—"}</td>
                      <td className="px-4 py-2 text-right space-x-1">
                        <button onClick={async () => {
                          // Try direct print; if reprint blocked → ask for reason
                          try {
                            await api.post(`/assets/${a.id}/print-record`, {});
                            toast.success("Sticker print recorded");
                            load();
                          } catch (e) {
                            const msg = formatApiErrorDetail(e.response?.data?.detail);
                            if (msg && msg.includes("approval")) {
                              setPrintReq({ asset: a, reason: "" });
                            } else { toast.error(msg); }
                          }
                        }} data-testid={`print-asset-${a.asset_number}`} className="text-xs border border-[#1565C0] text-[#1565C0] px-2 py-1 rounded-sm uppercase tracking-wider font-bold inline-flex items-center gap-1"><Printer size={12}/>Print</button>
                        {a.scrap_request_status === "pending" && isSA ? (
                          <>
                            <button onClick={() => decideScrap(a.id, "approve")} data-testid={`scrap-approve-${a.asset_number}`} className="text-xs bg-[#C62828] text-white px-2 py-1 rounded-sm uppercase tracking-wider font-bold">Approve Scrap</button>
                            <button onClick={() => { const n = window.prompt("Rejection note:"); if (n) decideScrap(a.id, "reject", n); }} data-testid={`scrap-reject-${a.asset_number}`} className="text-xs border border-[#0D2B5E] px-2 py-1 rounded-sm uppercase tracking-wider font-bold">Reject Scrap</button>
                          </>
                        ) : (
                          a.scrap_request_status !== "pending" && (
                            <button onClick={() => setScrap({ asset: a, reason: "" })} data-testid={`scrap-${a.asset_number}`} className="text-xs border border-[#C62828] text-[#C62828] px-2 py-1 rounded-sm uppercase tracking-wider font-bold inline-flex items-center gap-1"><Trash2 size={12}/>Scrap</button>
                          )
                        )}
                      </td>
                    </tr>
                  );
                })}
                {rows.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-sm text-[#0D2B5E]/50">No assets registered yet. Click <strong>New Asset</strong> to add one.</td></tr>}
              </tbody>
            </table>
            {assetControls}
          </div>
        </section>
      </div>

      {/* New Asset modal */}
      {open && (
        <Dialog title="New Asset" onClose={() => { setOpen(false); setForm(EMPTY()); }}>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-bold mb-1">Asset Number * <span className="text-[#0D2B5E]/40">(uppercase on save, 3–30 alnum+hyphen)</span></label>
              <input value={form.asset_number} onChange={(e) => setForm({ ...form, asset_number: e.target.value.toUpperCase() })}
                placeholder="e.g. GAUGE-001 or FIX-12A" data-testid="new-asset-number"
                className="w-full border border-[#EEEEEE] px-3 py-2 text-sm rounded-sm font-mono"/>
              {available && (
                <div className={`text-[10px] mt-1 uppercase tracking-widest font-bold ${available.available ? "text-[#2E7D32]" : "text-[#C62828]"}`} data-testid="asset-number-availability">
                  {available.available ? "✓ Available" : `✗ ${available.reason || "Taken"}`}
                </div>
              )}
            </div>
            <div>
              <label className="block text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-bold mb-1">Material (optional)</label>
              <select value={form.material_id} onChange={(e) => setForm({ ...form, material_id: e.target.value })} data-testid="new-asset-material"
                className="w-full border border-[#EEEEEE] px-3 py-2 text-sm rounded-sm">
                <option value="">— None (free-text only) —</option>
                {materials.map((m) => <option key={m.id} value={m.id}>{m.code} — {m.name}</option>)}
              </select>
            </div>
            <Input label="Location *" value={form.location} onChange={(v) => setForm({ ...form, location: v })} testId="new-asset-location"/>
            <Input label="Description" value={form.description} onChange={(v) => setForm({ ...form, description: v })} testId="new-asset-desc"/>
            <div>
              <label className="block text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-bold mb-1">Condition *</label>
              <div className="flex gap-2 flex-wrap">
                {CONDITIONS.map(([v, l, c]) => (
                  <button key={v} type="button" onClick={() => setForm({ ...form, condition: v })} data-testid={`new-asset-cond-${v}`}
                    className={`text-[10px] uppercase tracking-widest font-bold px-3 py-1.5 rounded-sm border-2 ${form.condition === v ? "text-white" : ""}`}
                    style={form.condition === v ? { backgroundColor: c, borderColor: c } : { borderColor: "#EEE", color: "#666" }}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
            {["damaged", "under_maintenance"].includes(form.condition) && (
              <Input label="Reason (mandatory)" value={form.condition_change_reason} onChange={(v) => setForm({ ...form, condition_change_reason: v })} testId="new-asset-cond-reason"/>
            )}
            <div className="col-span-2 flex items-center gap-3">
              <label className="inline-flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.calibration_required} onChange={(e) => setForm({ ...form, calibration_required: e.target.checked })} data-testid="new-asset-cal-toggle"/>
                Calibration required
              </label>
              {form.calibration_required && (
                <Input label="Calibration Due Date" type="date" value={form.calibration_due_date} onChange={(v) => setForm({ ...form, calibration_due_date: v })} testId="new-asset-cal-date"/>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-5">
            <button onClick={() => { setOpen(false); setForm(EMPTY()); }} className="px-4 py-2 border border-[#EEEEEE] text-sm rounded-sm">Cancel</button>
            <button onClick={save} disabled={available?.available === false || submitBusy}
              className="px-5 py-2 bg-[#1565C0] hover:bg-[#0D2B5E] disabled:bg-[#BBB] text-white text-sm rounded-sm font-bold uppercase tracking-wider"
              data-testid="new-asset-save">
              {submitBusy ? "Saving…" : "Save"}
            </button>
          </div>
        </Dialog>
      )}

      {/* Scrap request modal */}
      {scrap && (
        <Dialog title={`Request Scrap — ${scrap.asset.asset_number}`} onClose={() => setScrap(null)}>
          <div className="text-sm text-[#0D2B5E]/70 mb-3">Scrap requires Super Admin approval. Asset number will <strong>never</strong> be reused even after scrapping.</div>
          <label className="block text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-bold mb-1">Reason (min 5 chars)</label>
          <textarea value={scrap.reason} onChange={(e) => setScrap({ ...scrap, reason: e.target.value })} rows={3}
            className="w-full border border-[#EEEEEE] px-3 py-2 text-sm rounded-sm" data-testid="scrap-reason"/>
          <div className="flex justify-end gap-2 mt-5">
            <button onClick={() => setScrap(null)} className="px-4 py-2 border border-[#EEEEEE] text-sm rounded-sm">Cancel</button>
            <button onClick={requestScrap} className="px-5 py-2 bg-[#C62828] hover:bg-[#8B1B1B] text-white text-sm rounded-sm font-bold uppercase tracking-wider" data-testid="scrap-submit">
              Request Scrap
            </button>
          </div>
        </Dialog>
      )}

      {/* Reprint request modal */}
      {printReq && (
        <Dialog title={`Reprint Request — ${printReq.asset.asset_number}`} onClose={() => setPrintReq(null)}>
          <div className="text-sm text-[#0D2B5E]/70 mb-3">First print is free. Reprints need Plant Head / Admin approval.</div>
          <label className="block text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-bold mb-1">Reason</label>
          <textarea value={printReq.reason} onChange={(e) => setPrintReq({ ...printReq, reason: e.target.value })} rows={3}
            className="w-full border border-[#EEEEEE] px-3 py-2 text-sm rounded-sm" data-testid="reprint-reason"/>
          <div className="flex justify-end gap-2 mt-5">
            <button onClick={() => setPrintReq(null)} className="px-4 py-2 border border-[#EEEEEE] text-sm rounded-sm">Cancel</button>
            <button onClick={requestReprint} className="px-5 py-2 bg-[#1565C0] text-white text-sm rounded-sm font-bold uppercase tracking-wider" data-testid="reprint-submit">
              Send Request
            </button>
          </div>
        </Dialog>
      )}
    </div>
  );
}

function KV({ k, v, color }) {
  return (
    <div className="border-l-2 border-[#1565C0] pl-3">
      <div className="text-[10px] tracking-widest uppercase text-[#0D2B5E]/50 font-bold">{k}</div>
      <div className="text-lg font-bold font-mono" style={{ color: color || "#0D2B5E" }}>{v}</div>
    </div>
  );
}
